import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Avatar, Box, Typography, TextField, Button, Paper, Alert, useTheme, useMediaQuery, IconButton, InputAdornment } from '@mui/material';
import { ArrowBack, EmailOutlined, PersonOutline, PhoneOutlined, SaveOutlined } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI } from '../services/api';

const Profile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const attrs = user?.attributes || {};
  const displayName = [attrs.given_name, attrs.family_name].filter(Boolean).join(' ') || user?.username || 'Resident';
  
  const [formData, setFormData] = useState({
    firstName: attrs.given_name || '',
    lastName: attrs.family_name || ''
  });
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Update form data when user attributes change
  useEffect(() => {
    if (attrs.given_name || attrs.family_name) {
      setFormData({
        firstName: attrs.given_name || '',
        lastName: attrs.family_name || ''
      });
    }
  }, [attrs.given_name, attrs.family_name]);

  if (!user) return null;

  const handleBackToDashboard = () => {
    navigate('/');
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess('');
    setError('');
    setLoading(true);
    try {
      // Update Cognito attributes (excluding phone number as it's non-editable)
      const cognitoUser = window.userPool?.getCurrentUser();
      if (cognitoUser) {
        await new Promise((resolve, reject) => {
          cognitoUser.getSession((err, session) => {
            if (err || !session.isValid()) return reject('No valid session');
            
            // Prepare attributes to update (excluding phone number)
            const attributesToUpdate = [
              { Name: 'given_name', Value: formData.firstName },
              { Name: 'family_name', Value: formData.lastName }
            ];
            
            cognitoUser.updateAttributes(attributesToUpdate, (err, result) => {
              if (err) reject(err.message);
              else resolve(result);
            });
          });
        });
      }
      // Update backend user record (excluding phone number)
      const updateData = {
        firstName: formData.firstName,
        lastName: formData.lastName
      };
      
      await usersAPI.update(user.username, updateData);
      setSuccess(t('profile.profileUpdated'));
      
      // Update user state (excluding phone number)
      setUser({ 
        ...user, 
        attributes: { 
          ...attrs, 
          given_name: formData.firstName, 
          family_name: formData.lastName
        } 
      });
    } catch (err) {
      setError(err.message || t('profile.profileUpdateFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="mobile-content-spacing profile-section" sx={{ 
      width: '100%',
      maxWidth: isMobile ? 560 : 680,
      mx: 'auto', 
      mt: isMobile ? 0.5 : 2,
      px: isMobile ? 0.5 : 2,
      pb: 3
    }}>
      <Paper sx={{ 
        p: isMobile ? 1.75 : 3.5,
        boxShadow: isMobile ? '0 8px 28px rgba(15,23,42,.09)' : '0 14px 40px rgba(15,23,42,.1)',
        border: '1px solid #dfe7ef',
        borderRadius: 3,
        width: '100%',
        position: 'relative'
      }} elevation={0}>
        {/* Back button */}
        <IconButton
          onClick={handleBackToDashboard}
          sx={{
            position: 'absolute',
            top: isMobile ? 12 : 20,
            left: isMobile ? 12 : 20,
            color: 'primary.main',
            bgcolor: '#eaf4ff',
            zIndex: 1,
            '&:hover': { bgcolor: '#dceeff' }
          }}
          size="small"
        >
          <ArrowBack />
        </IconButton>
        
        <Typography 
          variant={isMobile ? "h5" : "h4"}
          sx={{ 
            textAlign: 'center',
            mb: isMobile ? 2 : 2.5,
            mt: 0,
            fontWeight: 800,
            color: '#0f172a'
          }}
        >
          Profile
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: { xs: 1.5, md: 2 }, mb: 2.5, borderRadius: 2.5, background: 'linear-gradient(135deg, #eef7ff 0%, #f9fcff 100%)', border: '1px solid #dbeafe' }}>
          <Avatar sx={{ width: { xs: 56, md: 68 }, height: { xs: 56, md: 68 }, bgcolor: '#0877dc', fontSize: { xs: 27, md: 31 }, boxShadow: '0 6px 14px rgba(8,119,220,.22)' }}>
            {displayName.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', md: '1.2rem' }, color: '#111827', textTransform: 'capitalize' }} noWrap>{displayName}</Typography>
            <Typography variant="body2" color="text.secondary">Personal and contact information</Typography>
          </Box>
        </Box>
        
        <Box>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: isMobile ? 2 : 2.5,
              mb: 2.5
            }}>
              <TextField
                label="First Name *"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                fullWidth
                required
                size="medium"
                variant="outlined"
                InputProps={{ startAdornment: <InputAdornment position="start"><PersonOutline color="action" /></InputAdornment> }}
                sx={{ 
                  '& .MuiInputLabel-root': {
                    fontSize: '16px',
                    fontWeight: 500
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '16px',
                    padding: '16px 14px'
                  },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    '& fieldset': {
                      borderColor: '#d0d0d0',
                      borderWidth: '1px'
                    },
                    '&:hover fieldset': {
                      borderColor: '#1976d2'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2',
                      borderWidth: '2px'
                    }
                  }
                }}
              />
              <TextField
                label="Last Name *"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                fullWidth
                required
                size="medium"
                variant="outlined"
                InputProps={{ startAdornment: <InputAdornment position="start"><PersonOutline color="action" /></InputAdornment> }}
                sx={{ 
                  '& .MuiInputLabel-root': {
                    fontSize: '16px',
                    fontWeight: 500
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '16px',
                    padding: '16px 14px'
                  },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    '& fieldset': {
                      borderColor: '#d0d0d0',
                      borderWidth: '1px'
                    },
                    '&:hover fieldset': {
                      borderColor: '#1976d2'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2',
                      borderWidth: '2px'
                    }
                  }
                }}
              />
              <TextField
                label="Phone Number *"
                value={attrs.phone_number || ''}
                fullWidth
                disabled
                size="medium"
                variant="outlined"
                InputProps={{ startAdornment: <InputAdornment position="start"><PhoneOutlined color="action" /></InputAdornment> }}
                sx={{ 
                  '& .MuiInputLabel-root': {
                    fontSize: '16px',
                    fontWeight: 500
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '16px',
                    padding: '16px 14px'
                  },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#d0d0d0',
                      borderWidth: '1px'
                    }
                  },
                  '& .Mui-disabled': {
                    backgroundColor: '#f8f8f8',
                    '& .MuiInputBase-input': {
                      color: '#666666',
                      '-webkit-text-fill-color': '#666666'
                    }
                  }
                }}
              />
              <TextField
                label="Email"
                value={attrs.email || ''}
                fullWidth
                disabled
                size="medium"
                variant="outlined"
                InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlined color="action" /></InputAdornment> }}
                sx={{ 
                  '& .MuiInputLabel-root': {
                    fontSize: '16px',
                    fontWeight: 500
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '16px',
                    padding: '16px 14px'
                  },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#d0d0d0',
                      borderWidth: '1px'
                    }
                  },
                  '& .Mui-disabled': {
                    backgroundColor: '#f8f8f8',
                    '& .MuiInputBase-input': {
                      color: '#666666',
                      '-webkit-text-fill-color': '#666666'
                    }
                  }
                }}
              />
            </Box>
            
            {/* Alerts */}
            {success && (
              <Alert 
                severity="success" 
                sx={{ 
                  mb: 2,
                  fontSize: '16px',
                  borderRadius: 2,
                  p: 2
                }}
              >
                {success}
              </Alert>
            )}
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  fontSize: '16px',
                  borderRadius: 2,
                  p: 2
                }}
              >
                {error}
              </Alert>
            )}
            
            {/* Buttons */}
            <Box sx={{ 
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: 1.5,
              alignItems: 'stretch',
              mt: 1
            }}>
              <Button
                variant="outlined"
                onClick={handleBackToDashboard}
                sx={{ 
                  minHeight: 50,
                  fontSize: '16px',
                  fontWeight: 500,
                  borderRadius: 2,
                  flex: isMobile ? 'none' : 1,
                  textTransform: 'none',
                  borderWidth: '2px',
                  '&:hover': {
                    borderWidth: '2px'
                  }
                }}
                fullWidth={isMobile}
              >
                Back to Dashboard
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                startIcon={!loading ? <SaveOutlined /> : null}
                sx={{ 
                  minHeight: 50,
                  fontSize: '16px',
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  flex: isMobile ? 'none' : 2,
                  boxShadow: '0 4px 8px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 12px rgba(25, 118, 210, 0.4)'
                  }
                }} 
                disabled={loading}
                fullWidth={isMobile}
              >
                {loading ? 'Updating...' : 'Update Profile'}
              </Button>
            </Box>
          </form>
        </Box>
      </Paper>
    </Box>
  );
};

export default Profile;
